import type { Manifest, ManifestAttr, ManifestDomain, ManifestRank } from "./types";

/**
 * Everything the UI knows about field labels, enum spellings and attribute
 * order comes from `/api/manifest`. Nothing in this file, and nothing in any
 * component, spells out a domain-specific label or enum value — a ninth domain
 * must appear correctly with no frontend change at all.
 */

export const EM_DASH = "—";

/** Values that mean "we looked, and the question doesn't apply". */
const SENTINEL_VALUES: ReadonlySet<string> = new Set(["unknown", "not_applicable", "n_a", "none"]);

/**
 * `least_concern` → "Least concern". Generic on purpose: it works on any enum
 * the registry invents, including ones that don't exist yet.
 */
export function humanizeEnum(value: string): string {
  const words = value.trim().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (words.length === 0) return "";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** True for snake_case-looking tokens, which are safe to humanise. */
function looksLikeToken(value: string): boolean {
  return /^[a-z0-9]+(?:[_-][a-z0-9]+)*$/.test(value);
}

export function domainFor(
  manifest: Manifest | undefined,
  domainKey: string | null,
): ManifestDomain | undefined {
  if (!manifest || !domainKey) return undefined;
  return manifest.domains.find((domain) => domain.key === domainKey);
}

export function domainLabel(manifest: Manifest | undefined, domainKey: string | null): string | null {
  const domain = domainFor(manifest, domainKey);
  if (domain) return domain.label;
  // Fall back to a humanised key rather than showing nothing at all.
  return domainKey ? humanizeEnum(domainKey) : null;
}

export function rankFor(
  manifest: Manifest | undefined,
  domainKey: string | null,
  rankKey: string | null,
): ManifestRank | undefined {
  if (!rankKey) return undefined;
  return domainFor(manifest, domainKey)?.ranks.find((rank) => rank.key === rankKey);
}

export function rankLabel(
  manifest: Manifest | undefined,
  domainKey: string | null,
  rankKey: string | null,
): string | null {
  const rank = rankFor(manifest, domainKey, rankKey);
  if (rank) return rank.label;
  return rankKey ? humanizeEnum(rankKey) : null;
}

/** The domain's blanket safety note, if it declares one. */
export function domainSafetyNote(
  manifest: Manifest | undefined,
  domainKey: string | null,
): string | null {
  return domainFor(manifest, domainKey)?.safetyNote ?? null;
}

/* -------------------------------------------------------------------------- */
/* Attribute presentation                                                     */
/* -------------------------------------------------------------------------- */

export type PresentedAttribute = {
  key: string;
  label: string;
  kind: ManifestAttr["kind"];
  prominent: boolean;
  /**
   * The display text. `null` means the attribute is genuinely absent — render
   * a blank ruled line if prominent, otherwise omit it.
   */
  display: string | null;
  /** For `string_list`, the individual items, already humanised. */
  items: string[] | null;
  /** `text` is long-form and wants a paragraph rather than a table row. */
  longForm: boolean;
};

function stringifyScalar(value: unknown): string | null {
  if (typeof value === "string") return value.trim().length > 0 ? value.trim() : null;
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return null;
}

function withUnit(text: string, unit: string | null): string {
  if (!unit) return text;
  return `${text} ${unit}`;
}

/**
 * Turn one raw attribute value into something printable, following the
 * contract's rendering rules to the letter.
 */
export function presentAttribute(attr: ManifestAttr, raw: unknown): PresentedAttribute {
  const base = {
    key: attr.key,
    label: attr.label,
    kind: attr.kind,
    prominent: attr.prominent,
    longForm: attr.kind === "text",
  };

  const absent: PresentedAttribute = { ...base, display: null, items: null };

  if (raw === null || raw === undefined) return absent;

  // A recorded "we don't know" is not the same as an unrecorded field: it
  // prints as an em dash rather than an inviting blank line.
  if (typeof raw === "string" && SENTINEL_VALUES.has(raw.trim().toLowerCase())) {
    return { ...base, display: EM_DASH, items: null };
  }

  if (attr.kind === "string_list") {
    if (!Array.isArray(raw)) {
      const single = stringifyScalar(raw);
      return single === null ? absent : { ...base, display: single, items: [single] };
    }
    const items = raw
      .map((item) => stringifyScalar(item))
      .filter((item): item is string => item !== null)
      .filter((item) => !SENTINEL_VALUES.has(item.toLowerCase()))
      .map((item) => (looksLikeToken(item) ? humanizeEnum(item) : item));
    if (items.length === 0) return absent;
    return { ...base, display: items.join(", "), items };
  }

  if (attr.kind === "enum") {
    const token = stringifyScalar(raw);
    if (token === null) return absent;
    return { ...base, display: humanizeEnum(token), items: null };
  }

  if (attr.kind === "boolean") {
    if (typeof raw === "boolean") return { ...base, display: raw ? "Yes" : "No", items: null };
    const token = stringifyScalar(raw);
    if (token === null) return absent;
    if (token === "true" || token === "false") {
      return { ...base, display: token === "true" ? "Yes" : "No", items: null };
    }
    return { ...base, display: token, items: null };
  }

  const scalar = stringifyScalar(raw);
  if (scalar === null) {
    // An object or array under a scalar kind: show it rather than lose it.
    if (typeof raw === "object") {
      return { ...base, display: JSON.stringify(raw), items: null };
    }
    return absent;
  }

  return { ...base, display: withUnit(scalar, attr.unit), items: null };
}

/**
 /**
  * The one attribute worth showing beside the common and scientific name — a
  * single, domain-chosen fact (native status, size, edibility, style…), never
  * a list. Returns `null` when the domain declares none, or the entry hasn't
  * got a value for it.
  */
 export function headlineAttribute(
   manifest: Manifest | undefined,
   domainKey: string | null,
   attributes: Record<string, unknown>,
 ): PresentedAttribute | null {
   const domain = domainFor(manifest, domainKey);
   const key = domain?.headlineAttr;
   if (!domain || !key) return null;

   const attr = domain.attrs.find((candidate) => candidate.key === key);
   if (!attr) return null;

   const presented = presentAttribute(attr, attributes[key]);
   return presented.display !== null ? presented : null;
 }

 /** The attributes to render for an entry, in the manifest's deliberate order —
 * it reads like a field guide, so it is never re-sorted here.
 *
 * Non-prominent empties are dropped. Prominent empties survive as `display:
 * null` so the table can rule a blank line for them.
 */
export function presentAttributes(
  manifest: Manifest | undefined,
  domainKey: string | null,
  attributes: Record<string, unknown>,
): PresentedAttribute[] {
  const domain = domainFor(manifest, domainKey);

  if (!domain) {
    // No manifest entry for this domain yet (a brand new domain, or the
    // manifest hasn't loaded). Show what we have, keyed order, humanised keys.
    return Object.entries(attributes)
      .map(([key, raw]) =>
        presentAttribute(
          { key, label: humanizeEnum(key), kind: "string", choices: [], prominent: false, unit: null },
          raw,
        ),
      )
      .filter((presented) => presented.display !== null);
  }

  const known = new Set(domain.attrs.map((attr) => attr.key));

  const ordered = domain.attrs
    .map((attr) => presentAttribute(attr, attributes[attr.key]))
    .filter((presented) => presented.display !== null || presented.prominent);

  // Anything the entry carries that the manifest doesn't describe yet — keep
  // it, at the end, rather than silently dropping data.
  const extras = Object.entries(attributes)
    .filter(([key]) => !known.has(key))
    .map(([key, raw]) =>
      presentAttribute(
        { key, label: humanizeEnum(key), kind: "string", choices: [], prominent: false, unit: null },
        raw,
      ),
    )
    .filter((presented) => presented.display !== null);

  return [...ordered, ...extras];
}

/** Split presented attributes into table rows and long-form paragraphs. */
export function partitionAttributes(presented: readonly PresentedAttribute[]): {
  rows: PresentedAttribute[];
  paragraphs: PresentedAttribute[];
} {
  const rows: PresentedAttribute[] = [];
  const paragraphs: PresentedAttribute[] = [];
  for (const attribute of presented) {
    if (attribute.longForm && attribute.display !== null) paragraphs.push(attribute);
    else rows.push(attribute);
  }
  return { rows, paragraphs };
}
